/**
 * Safe Navigation Hook
 * 
 * Provides navigation functions with automatic fallback to frontpage
 * and error handling with toaster messages.
 */

import {useNavigate} from 'react-router-dom';
import {useStoreState} from '@/store';
import {getRootRedirectUrl} from '@/utils/rootUrlResolver';
import {useAlert} from '@/components/alerts';
import i18n from 'i18n';

export interface SafeNavigationOptions {
  /** Show error message in toaster */
  showErrorMessage?: boolean;
  /** Custom error message */
  errorMessage?: string;
  /** Replace current history entry instead of pushing new one */
  replace?: boolean;
}

export function useSafeNavigation() {
    const navigate = useNavigate();
    const {showError, showWarning} = useAlert();
  
    // Get user data and menu structure for intelligent redirect
    const userData = useStoreState(state => state.userData.data);
    const menuStructure = useStoreState(state => state.sidebarMenu.menuStructure);

    /**
   * Navigate to root/frontpage with fallback handling
   */
    const navigateToRoot = (options: SafeNavigationOptions = {}) => {
        const {showErrorMessage = false, errorMessage, replace = false} = options;
    
        try {
            const rootUrl = getRootRedirectUrl({userData, menuStructure});
            navigate(rootUrl, {replace});
        } catch (error) {
            console.error('🧭 useSafeNavigation: Failed to navigate to root', error);
      
            if (showErrorMessage) {
                const message = errorMessage || i18n.t('navigation_error.text', 'Navigation error occurred. Redirecting to home.');
                showError(message);
            }
      
            // Fallback navigation
            navigate('/app/home/welcome', {replace});
        }
    };

    /**
   * Navigate with automatic fallback on error
   */
    const navigateWithFallback = (
        url: string, 
        options: SafeNavigationOptions = {}
    ) => {
        const {showErrorMessage = true, errorMessage, replace = false} = options;
    
        try {
            navigate(url, {replace});
        } catch (error) {
            console.error('🧭 useSafeNavigation: Navigation failed, using fallback', error);
      
            if (showErrorMessage) {
                const message = errorMessage || i18n.t('page_not_found_redirecting.text', 'Page not found. Redirecting to dashboard...');
                showWarning(message);
            }
      
            // Fallback to root
            navigateToRoot({showErrorMessage: false, replace});
        }
    };

    /**
   * Handle navigation errors (e.g., from route guards)
   */
    const handleNavigationError = (
        error: Error,
        options: SafeNavigationOptions = {}
    ) => {
        const {errorMessage} = options;
    
        console.error('🧭 useSafeNavigation: Navigation error handled', error);
    
        const message = errorMessage || i18n.t('access_denied_redirecting.text', 'Access denied. Redirecting to dashboard...');
        showError(message);
    
        // Redirect to safe location
        navigateToRoot({showErrorMessage: false, replace: true});
    };

    /**
   * Navigate back with fallback
   */
    const navigateBack = (fallbackUrl?: string) => {
        try {
            if (window.history.length > 1) {
                window.history.back();
            } else {
                // No history, navigate to fallback or root
                if (fallbackUrl) {
                    navigate(fallbackUrl);
                } else {
                    navigateToRoot();
                }
            }
        } catch (error) {
            console.error('🧭 useSafeNavigation: Back navigation failed', error);
            navigateToRoot();
        }
    };

    return {
        navigateToRoot,
        navigateWithFallback,
        handleNavigationError,
        navigateBack,
        // Expose the raw navigate function for advanced use cases
        navigate,
    };
}

export default useSafeNavigation;